import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * ⚠ BU DOSYA KURAL TAŞIMAZ (bkz. `compliance-api.ts` başlığındaki aynı
 * doktrin). Asistanın araç seçimi, hukuki niteleme sınırları ve onay
 * gerektiren adımlar SUNUCUDADIR (`assistant-tool-registry.ts`); burada
 * yalnız sunucunun gönderdiği şeklin tipi ve ekranın okuyacağı adlar var.
 */

export interface AssistantConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

/** Anthropic içerik bloğunun panelin bildiği KADARI — geri kalan alanlar yok sayılır. */
export interface AssistantContentBlock {
  type: string;
  text?: string;
  name?: string;
  input?: unknown;
  is_error?: boolean;
  [key: string]: unknown;
}

export interface AssistantMessageRow {
  id: string;
  conversationId: string;
  sequence: number;
  role: 'user' | 'assistant';
  content: AssistantContentBlock[];
  createdAt: string;
}

export interface AssistantConversationDetail {
  conversation: AssistantConversationSummary;
  messages: AssistantMessageRow[];
}

export interface AssistantStep {
  tool: string;
  ok: boolean;
  deepLink?: string;
}

export interface AssistantTurnResult {
  conversationId: string;
  message: string;
  steps: AssistantStep[];
}

/** Sohbet ekranının çizdiği tek bir balon — ham blok dizisinden türetilir. */
export interface DisplayMessage {
  key: string;
  role: 'user' | 'assistant';
  text: string;
  /** Yalnız asistan mesajları için: bu turda çağrılan araçlar. */
  toolCalls: Array<{ name: string }>;
}

/**
 * Ham `assistant_messages` satırlarını sohbet balonlarına çevirir.
 *
 * ⚠ `role='user'` İKİ ŞEKİL TAŞIYABİLİR: kullanıcının yazdığı metin YA DA bir
 * önceki turun araç sonuçları (Anthropic'in mesaj modelinde ikisi de `user`
 * rolündedir). Araç sonucu satırları burada GÖSTERİLMEZ — kullanıcı onları
 * yazmadı; o turun hangi araçları çağırdığı, asistan mesajının kendi
 * `tool_use` bloklarından zaten görünür.
 */
export function toDisplayMessages(rows: AssistantMessageRow[]): DisplayMessage[] {
  // ⚠ SAVUNMA: `active.data` her zaman `AssistantConversationDetail` OLMAK
  // ZORUNDA değil (bkz. `useAssistantConversation`teki anahtar çakışması
  // notu) — dizi olmayan bir değer burada SESSİZCE boş listeye düşer, ham
  // bir "is not iterable" ile beyaz ekran ÜRETMEZ.
  if (!Array.isArray(rows)) return [];
  const out: DisplayMessage[] = [];
  for (const row of rows) {
    const textBlocks = row.content.filter((b) => b.type === 'text' && typeof b.text === 'string');
    const text = textBlocks
      .map((b) => b.text)
      .join('\n')
      .trim();

    if (row.role === 'user') {
      // Araç sonucu satırı — kullanıcıya gösterilecek bir şey yok.
      if (row.content[0]?.type === 'tool_result') continue;
      if (!text) continue;
      out.push({ key: row.id, role: 'user', text, toolCalls: [] });
      continue;
    }

    const toolCalls = row.content
      .filter((b) => b.type === 'tool_use' && typeof b.name === 'string')
      .map((b) => ({ name: b.name! }));
    if (!text && toolCalls.length === 0) continue;
    out.push({ key: row.id, role: 'assistant', text, toolCalls });
  }
  return out;
}

export const assistantKeys = {
  all: ['assistant'] as const,
  conversations: () => ['assistant', 'conversations'] as const,
  conversation: (id: string) => ['assistant', 'conversations', id] as const,
};

export function useAssistantConversations() {
  return useQuery({
    queryKey: assistantKeys.conversations(),
    queryFn: () => api.get<AssistantConversationSummary[]>('/assistant/conversations'),
  });
}

/**
 * ⚠⚠ ÖLÇÜLMÜŞ ARIZA (2026-09-16, canlıda beyaz ekran): `id === null`
 * durumunda anahtar `assistantKeys.conversations()`e (`['assistant',
 * 'conversations']`) DÜŞÜYORDU — bu SATIR BİREBİR `useAssistantConversations`
 * ile AYNI anahtardı. TanStack Query önbelleği anahtara göre KÜRESELDİR;
 * `enabled: false` yalnız YENİ BİR ÇEKMEyi engeller, o anahtarda ZATEN
 * duran veriyi OKUMAYI engellemez. Sonuç: "yeni sohbet" moduna geçildiğinde
 * bu kanca, komşu `useAssistantConversations`in döndürdüğü DİZİYİ
 * `AssistantConversationDetail` sanıp döndürüyordu ve
 * `toDisplayMessages(dizi.messages)` — `dizi.messages` `undefined` olduğu
 * için — "is not iterable" ile ÇÖKÜYORDU.
 *
 * Düzeltme: `id === null`de anahtar gerçek hiçbir konuşma kimliğiyle
 * ÇAKIŞAMAYACAK bir değere düşer (`'__yeni__'`, uuid biçiminde DEĞİL).
 */
export function useAssistantConversation(id: string | null) {
  return useQuery({
    queryKey: assistantKeys.conversation(id ?? '__yeni__'),
    queryFn: () => api.get<AssistantConversationDetail>(`/assistant/conversations/${id}`),
    enabled: id !== null,
  });
}

/**
 * ⚠ `retry: false` — `query.ts`teki mutasyon varsayılanıyla AYNI gerekçe
 * (`mutations.retry: false`, "bir mutasyon otomatik tekrar edilmez"): bir
 * turu kör yeniden denemek aynı soruyu iki kez sormak, hatta iki taslak
 * doğurmak demek olabilir.
 */
export function useAssistantTurn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { conversationId?: string; message: string }) =>
      api.post<AssistantTurnResult>('/assistant/turn', input),
    onSuccess: (result) => {
      // ⚠ `setQueryData(key, undefined)` BURADA DURUYORDU VE ÖLÜ KODDU:
      // query-core `functionalUpdate` sonucu `undefined` ise hiçbir şey
      // yazmadan döner (`queryClient.js`: `if (data === void 0) return`), yani
      // önbellek hiç temizlenmiyordu. Çalışsaydı daha kötü olurdu: tüm
      // yazışma silinip yerine bir spinner gelirdi. Doğru davranış zaten
      // aşağıdaki `invalidateQueries`in yaptığı şeydir — bayat veriyi ekranda
      // tutup arkada tazelemek.
      void qc.invalidateQueries({ queryKey: assistantKeys.conversation(result.conversationId) });
      void qc.invalidateQueries({ queryKey: assistantKeys.conversations() });
    },
  });
}
